// Finnish UI strings (default). `Messages` is derived from this object; en.ts must match it.
// Labels wrap and are never truncated: write them in full.
import type { Case, FlagKind, Gender, GramNumber, WordClass } from '@valyrio/engine/types';
import type { AreaMessages } from './area';
import { areasFi } from './area-registry';

/** The strings outside the areas (areas: i18n/areas/*.ts, added below). */
const fiCore = {
  appName: 'Valyrio udrir',
  nav: {
    label: 'Näkymät',
    today: 'Tänään',
    free: 'Vapaa harjoitus',
    lessons: 'Oppitunnit',
    grammar: 'Kielioppi',
    dictionary: 'Sanakirja',
    translator: 'Kääntäjä',
    letters: 'Kirjeet',
    review: 'Tarkistusjono',
    ownWords: 'Omat sanat',
    sentenceStack: 'Lausepino',
    // Meidän nimet (routes/Names.nav.ts)
    names: 'Meidän nimet',
    // Lukupino (routes/Reading.nav.ts)
    reading: 'Lukupino',
    pronunciation: 'Ääntäminen',
    listen: 'Sarjan repliikit',
    shared: 'Yhteinen sessio',
    settings: 'Asetukset',
    more: 'Lisää',
    groups: {
      practice: 'Harjoittele',
      learn: 'Opi',
      find: 'Hae ja käännä',
      together: 'Yhdessä',
      app: 'Sovellus',
    },
  },
  sync: {
    offline: 'Ei yhteyttä',
    /** Phone top bar: one line that never wraps; the full sentence is read out and in the tooltip. */
    offlineShort: (n: number): string => (n ? `Ei yhteyttä · ${n} odottaa` : 'Ei yhteyttä'),
    pendingShort: (n: number): string => `${n} odottaa`,
    pending: (n: number): string => (n === 1 ? '1 muutos odottaa lähetystä' : `${n} muutosta odottaa lähetystä`),
    syncing: 'Tallennetaan…',
    saved: 'Tallennettu',
    auth: 'Kirjaudu uudelleen',
    error: 'Tallennus ei onnistunut',
    retry: 'Yritä uudelleen',
  },
  theme: {
    label: 'Teema',
    system: 'Auto',
    light: 'Vaalea',
    dark: 'Tumma',
  },
  lang: { label: 'Käyttöliittymän kieli', fi: 'Suomi', en: 'English' },
  frontier: {
    lessonOpen: (n: number) => `Oppitunti ${n} avoinna teille molemmille`,
    notStarted: (date: string) => `Kurssi alkaa ${date}`,
    next: (date: string) => `seuraava ${date}`,
    partnerToday: (name: string, n: number) => `${name}: ${n} tänään`,
    partnerNotYet: (name: string) => `${name}: ei vielä tänään`,
    youToday: (n: number) => `Sinä: ${n} tänään`,
    ground: 'vapaa harjoitus',
    paused: 'Uudet sanat odottavat, kunnes rästit on kerrattu.',
    demo: 'demo',
    partner: 'Kumppani',
  },
  progress: {
    label: 'Istunnon eteneminen',
    count: (i: number, n: number) => `${i} / ${n}`,
  },
  memory: { new: 'uusi', settling: 'vakiintumassa', steady: 'vakaa' },
  card: {
    label: (i: number, n: number) => `Kortti ${i} / ${n}`,
    showAnswer: 'Näytä vastaus',
    spaceKey: 'Välilyönti',
    check: 'Tarkista',
    dontKnow: 'En tiedä',
    typeHv: 'Kirjoita valyriaksi',
    typeHint: 'Alt + a, e, i, o, u tai y kirjoittaa pitkän vokaalin, Shift ison kirjaimen.',
    typeFirst: 'Kirjoita vastaus ensin.',
    ok: 'Oikein.',
    near: 'Melkein – vain vokaalin pituus tai tarkkeet eroavat.',
    wrong: 'Ei aivan. Oikea muoto on alla.',
    skip: 'Tässä oikea muoto.',
    youTyped: 'Kirjoitit',
    alsoRight: (form: string) => `Myös ${form} käy.`,
    /** A keyboard suggestion was picked in the answer (lib/assisted.ts): at most Vaikea / ½. */
    assisted: 'Näppäimistön ehdotuksen avulla.',
    toHv: 'valyriaksi',
    kind: {
      'hv-native': 'sana',
      'native-hv': 'käännös suomesta valyriaksi',
      'form-drill': 'muotoharjoitus',
      sentence: 'lause kääntäjästä',
      listen: 'kuuntelukortti',
    },
    typingFrom: (src: 'fi' | 'en') => `käännös ${src === 'fi' ? 'suomesta' : 'englannista'} valyriaksi`,
    newWord: 'uusi sana',
    noMeaning: 'Merkitys puuttuu.',
    // sentence cards from the translator (phase 3 item 19)
    sentenceOfficial: 'virallinen käännös',
    sentenceSure: 'kääntäjän varma käännös',
    sentenceRecall: 'Mitä lause tarkoittaa?',
  },
  grades: {
    label: 'Kuinka hyvin muistit?',
    names: { 1: 'Uudelleen', 2: 'Vaikea', 3: 'Osasin', 4: 'Helppo' } as Record<1 | 2 | 3 | 4, string>,
    returns: (iv: string) => `palaa ${iv}`,
  },
  reveal: {
    interlinear: 'Morfeemijako',
    example: 'Esimerkki',
    wikiExample: 'wikin esimerkki',
    noSeam: 'Tämän sanan vartalon ja päätteen rajaa ei ole vielä varmistettu, joten saumaa ei näytetä.',
  },
  ours: 'meidän',
  /** Before the wiki's footnote on a show line (in English), e.g. Peterson's own mistake. */
  lineNote: 'Wikin alaviite:',
  pos: {
    noun: 'substantiivi',
    'proper-noun': 'erisnimi',
    pronoun: 'pronomini',
    adjective: 'adjektiivi',
    verb: 'verbi',
    adverb: 'adverbi',
    numeral: 'numeraali',
    conjunction: 'konjunktio',
    postposition: 'postpositio',
    preposition: 'prepositio',
    interjection: 'interjektio',
    particle: 'partikkeli',
    determiner: 'determinantti',
    phrase: 'fraasi',
    affix: 'affiksi',
    other: 'muu',
  } as Record<WordClass, string>,
  gender: { lunar: 'kuusuku', solar: 'aurinkosuku', terrestrial: 'maasuku', aquatic: 'vesisuku' } as Record<Gender, string>,
  declension: (d: string) => (/^\d+$/.test(d) ? `${d}. deklinaatio` : d === 'irregular' ? 'epäsäännöllinen' : d),
  cases: {
    nom: 'nominatiivi',
    acc: 'akkusatiivi',
    gen: 'genetiivi',
    dat: 'datiivi',
    loc: 'lokatiivi',
    ins: 'instrumentaali',
    com: 'komitatiivi',
    voc: 'vokatiivi',
  } as Record<Case, string>,
  numbers: { sg: 'yksikkö', pl: 'monikko', pc: 'muutama', col: 'kollektiivi', pcpl: 'muutaman monikko', colpl: 'kollektiivin monikko' } as Record<
    GramNumber,
    string
  >,
  flag: {
    button: 'Merkitse tarkistettavaksi',
    title: 'Merkitse tarkistettavaksi',
    kind: 'Mistä on kyse?',
    kinds: {
      'fi-meaning': 'Suomenkielinen merkitys',
      'en-meaning': 'Englanninkielinen merkitys',
      translator: 'Kääntäjän tulos',
      'missing-word': 'Puuttuva sana',
      grammar: 'Kielioppi',
      lesson: 'Oppitunti',
      segmentation: 'Sauma eli morfeemijako',
      form: 'Taivutusmuoto',
      example: 'Esimerkki',
      bug: 'Virhe sovelluksessa',
      idea: 'Idea',
      'official-change': 'Virallinen tieto on muuttunut',
      'ours-superseded': 'Petersonilla on sana samalle merkitykselle',
      'ours-collision': 'Sama kirjoitusasu kuin omalla sanalla',
      'need-answered': 'Petersonilla on sana, jota tarvitsette',
    } as Record<FlagKind, string>,
    note: 'Huomautus',
    notePlaceholder: 'esim. pitäisikö tämä kääntää …',
    suggested: 'Ehdotus (valinnainen)',
    suggestedPlaceholder: 'esim. parempi käännös tai muoto',
    save: 'Tallenna merkintä',
    cancel: 'Peru',
    noteRequired: 'Kirjoita lyhyt huomautus ennen tallennusta.',
    flagged: 'Merkitty tarkistettavaksi · avoin',
    queued: 'Lähtee, kun yhteys palaa.',
    another: 'Lisää toinen huomautus',
    flaggedHandled: 'Merkitty tarkistettavaksi · käsitelty sessiossa',
    flaggedBy: (name: string) => `merkitsi ${name}`,
  },
  macron: { group: 'Pitkät vokaalit', shift: 'Iso kirjain' },
  sheet: { close: 'Sulje' },
  end: {
    door: 'Vapaa harjoitus – vain avatut sanat',
    more: 'Jatka seuraavilla heikoimmilla sanoilla',
    ledger: 'Tämän istunnon kortit',
  },
  today: {
    nothingDue: 'Päivän kortit on tehty.',
    nothingYet: 'Ei kortteja vielä.',
    notStarted: (date: string) => `Kurssi alkaa ${date.replace(/\.$/, '')}. Ensimmäinen oppitunti avautuu teille molemmille samana päivänä.`,
    paused: 'Uudet sanat odottavat, kunnes rästit on kerrattu. Kertaukset jatkuvat normaalisti.',
    intro: (n: number) => (n === 0 ? 'Ei uusia sanoja tänään.' : n === 1 ? '1 uusi sana tänään.' : `${n} uutta sanaa tänään.`),
    loading: 'Ladataan sanakirjaa…',
    fixture: 'Testisanakirja: oikeaa sanakirjaa ei ole vielä ladattu.',
  },
  free: {
    title: 'Vapaa harjoitus',
    line: 'Vapaa harjoitus · vain avatut sanat',
    empty: 'Vapaassa harjoituksessa kerrataan sanoja, jotka olet jo nähnyt. Aloita päivän korteista.',
    start: 'Aloita',
  },
  record: {
    title: 'Yhteinen kirjanpito',
    /** Label after the bold count: agrees with the number. */
    days: (n: number): string => (n === 1 ? 'opiskelupäivä' : 'opiskelupäivää'),
    together: (n: number) => `${n} ${n === 1 ? 'päivä' : 'päivää'} yhdessä`,
    steady: (n: number): string => (n === 1 ? 'vakaa sana' : 'vakaata sanaa'),
    you: 'Sinä',
  },
  settings: {
    title: 'Asetukset',
    appearance: 'Ulkoasu ja kieli',
    cursor: 'Hiiren osoitin',
    cursorHv: 'Valyrialainen',
    cursorSystem: 'Järjestelmän',
    answers: 'Vastaukset',
    study: 'Opiskelu',
    reminder: 'Muistutus',
    reminderExplain: 'Yksi lempeä muistutus päivässä – vain jos et ole vielä opiskellut sinä päivänä.',
    reminderTime: 'Muistutusaika',
    reminderOff: 'Ei muistutusta',
    reminderSave: 'Tallenna aika',
    pace: 'Yhteinen tahti',
    /** Who sets a shared setting (pace, reminder time). */
    setBy: (name: string) => `Tämän asettaa ${name} teille molemmille.`,
    setByYou: 'Asetat tämän teille molemmille Ylläpito-osiossa.',
    theAdmin: 'ylläpitäjä',
    lastChange: (date: string, name: string) => `Muutettu viimeksi ${date} (${name}).`,
    saved: 'Tallennettu. Muutos näkyy teille molemmille.',
    savePace: 'Tallenna tahti',
    shared: 'Yhteiset asetukset',
    sharedNote: 'Nämä koskevat teitä molempia, ja muutos tulee voimaan heti.',
    /** Concrete examples under each setting (what it does in practice). */
    ex: {
      theme: 'Auto seuraa laitteen omaa asetusta.',
      cursor:
        'Valyrialainen: basaltinmusta nuoli, jonka varressa on sininen hitsausmerkki; linkkien ja painikkeiden päällä samanlainen käsi. Järjestelmän: tietokoneen oma osoitin. Valinta koskee vain tätä laitetta.',
      macronNote: 'Lempeä: {wrong} (oikein {right}) lasketaan oikeaksi, ja näet huomautuksen ”Oikein – tarkista vielä pituusmerkit”.',
      macronStrict: 'Tarkka: {wrong} on ”melkein oikein” ja saa ½ pistettä.',
      reminderAt: (time: string) => `Muistutus tulee noin klo ${time} teille molemmille – kummallekin vain, jos ei ole vielä opiskellut sinä päivänä.`,
      reminderNone: 'Yhteistä muistutusaikaa ei ole asetettu: iltamuistutus tulee vain, jos sille on asetettu oma aika kohdassa Omat muistutukset.',
      paceDaily: (first: number, days: number, after: number) =>
        `Ensimmäiset ${days} päivää ${first} ${first === 1 ? 'uusi sana' : 'uutta sanaa'} päivässä (noin ${first * 7} viikossa), sen jälkeen ${after} päivässä (noin ${after * 7} viikossa).`,
      paceLessons: (n: number) =>
        n === 1
          ? 'Uusi oppitunti avautuu joka lauantai, kun olette molemmat läpäisseet edellisen.'
          : `Oppitunteja avautuu ${n} viikossa lauantaisin, kun olette molemmat läpäisseet edelliset.`,
      pacePause: (n: number) => `Jos jommallakummalla on yli ${n} rästikorttia, uudet sanat odottavat, kunnes rästit on kerrattu.`,
    },
    startDate: 'Aloituspäivä (lauantai)',
    newFirst: 'Uusia sanoja päivässä alkujaksolla',
    firstMonthDays: 'Alkujakson pituus päivinä',
    newAfter: 'Uusia sanoja päivässä alkujakson jälkeen',
    lessonsPerWeek: 'Oppitunteja viikossa',
    pauseAbove: 'Uudet sanat tauolle, kun rästikortteja on yli',
    needsOnline: 'Yhteisten asetusten muuttaminen vaatii yhteyden.',
    notSaturday: 'Aloituspäivän on oltava lauantai.',
    account: 'Tili',
    signedInAs: (name: string, username: string) => `Kirjautuneena: ${name}${username ? ` (käyttäjätunnus ${username})` : ''}`,
    signOut: 'Kirjaudu ulos',
    signOutChecking: 'Lähetetään odottavat muutokset…',
    signOutPending: (n: number): string =>
      n === 1
        ? '1 muutos ei ole vielä lähtenyt palvelimelle. Jos kirjaudut ulos nyt, se katoaa tältä laitteelta. Kirjaudutaanko ulos silti?'
        : `${n} muutosta ei ole vielä lähtenyt palvelimelle. Jos kirjaudut ulos nyt, ne katoavat tältä laitteelta. Kirjaudutaanko ulos silti?`,
    signOutAnyway: 'Kirjaudu ulos silti',
    signOutOffline: 'Uloskirjautuminen vaatii yhteyden: ilman sitä istunto jää palvelimelle voimaan. Yritä uudelleen, kun olet verkossa.',
    flagApp: 'Huomautus sovelluksesta',
    /** Ylläpito: the last section, only for the admin (lib/people.ts isAdmin). */
    admin: 'Ylläpito',
    technical: 'Tekniset tiedot',
    pushTest: 'Testi-ilmoitus',
    pushTestNeedsOn: 'Ota muistutukset ensin käyttöön tällä laitteella, niin voit lähettää testin.',
    dictionary: 'Sanakirja',
    dictLine: (n: number, date: string) => `${n.toLocaleString('fi-FI')} ${n === 1 ? 'sana' : 'sanaa'} · versio ${date}`,
    fixture: 'testisanakirja',
    unsent: (n: number): string => (n === 1 ? '1 muutos odottaa lähetystä.' : `${n} muutosta odottaa lähetystä.`),
    rejected: (n: number): string =>
      n === 1 ? 'Palvelin hylkäsi 1 muutoksen. Näet sen tarkistusjonossa.' : `Palvelin hylkäsi ${n} muutosta. Näet ne tarkistusjonossa.`,
    demo: 'Demotila: palvelinta ei tavoitettu, joten näet esimerkkikäyttäjän. Mitään ei lähetetä.',
  },
  update: {
    available: 'Uusi versio on saatavilla.',
    reload: 'Päivitä',
    later: 'Myöhemmin',
    offlineReady: 'Sovellus toimii nyt myös ilman yhteyttä.',
    ok: 'Selvä',
    // update gate (shell/UpdateGate.tsx)
    checking: 'Tarkistetaan päivityksiä…',
    updating: 'Päivitetään…',
  },
  more: { title: 'Lisää' },
  /** "Odottaa sinua": a quiet line on Tänään and counts in Lisää (from SyncPull.inbox). */
  inbox: {
    label: 'Odottaa sinua',
    proposals: (n: number): string => (n === 1 ? 'sana hyväksyttäväksi' : `${n} sanaa hyväksyttäväksi`),
    picks: (n: number): string => (n === 1 ? 'ehdokas valittavaksi' : `${n} tarvetta, joille voi valita ehdokkaan`),
    handledFlags: (n: number): string => (n === 1 ? 'käsitelty merkintä suljettavaksi' : `${n} käsiteltyä merkintää suljettavaksi`),
    letters: (n: number): string => (n === 1 ? 'uusi kirje' : `${n} uutta kirjettä`),
    corrections: (n: number): string => (n === 1 ? 'korjaus kirjeeseesi' : `${n} korjausta kirjeisiisi`),
    count: (n: number): string => `${n} odottaa`,
  },
  common: {
    loading: 'Ladataan…',
    error: 'Tietojen haku ei onnistunut.',
    retry: 'Yritä uudelleen',
    offlineCopy: (when: string) => `Ei yhteyttä · tiedot on haettu ${when}.`,
    offlineEmpty: 'Ei yhteyttä. Tämä näkymä tarvitsee yhteyden, kun avaat sen ensimmäisen kerran.',
    auth: 'Kirjautuminen on vanhentunut. Lataa sivu uudelleen ja kirjaudu.',
    demo: 'Demotila: palvelinta ei tavoitettu, joten tässä näkymässä ei ole tietoja.',
    needsOnline: 'Tämä vaatii yhteyden.',
    queued: 'Lähtee, kun yhteys palaa.',
    you: 'Sinä',
    partner: 'Kumppani',
    session: 'Tarkistussessio',
    save: 'Tallenna',
    cancel: 'Peru',
    edit: 'Muokkaa',
    close: 'Sulje',
    back: 'Takaisin',
    send: 'Lähetä',
    delete: 'Poista',
    more: 'Näytä lisää',
    indexBuilding: (pct: number) => `Taivutusmuotojen hakemisto on vielä kesken (${pct}\u00a0%). Perusmuodot ja merkitykset löytyvät jo.`,
    indexError: 'Taivutusmuotojen hakemistoa ei saatu valmiiksi. Perusmuodot ja merkitykset löytyvät silti.',
  },
  audio: {
    play: 'Kuuntele',
    pause: 'Tauko',
    loop: 'Silmukka',
    loopOn: 'Silmukka päällä',
    slow: 'Hidas 0,75×',
    peterson: 'Petersonin äänite',
    file: (n: number, of: number) => `äänite ${n}/${of}`,
    failed: 'Äänitettä ei voitu toistaa.',
  },
  stress: {
    label: 'Paino',
    hint: 'Painollinen tavu on kirjoitettu isoin kirjaimin.',
    none: 'yksitavuinen',
  },
  show: { got: 'GoT', hotd: 'HotD', telltale: 'Telltale', other: '' } as Record<'got' | 'hotd' | 'telltale' | 'other', string>,
};

/** The strings outside the areas, whose shape en.ts must match. */
export type CoreMessages = typeof fiCore;
/** Every UI string: the core and the areas (each area file adds its key to AreaMessages). */
export type Messages = CoreMessages & AreaMessages;

export const fi: Messages = { ...fiCore, ...areasFi };
